import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { BACKEND_URL } from '@/lib/api';

// Generate or get anonymous session ID
const getAnonymousSessionId = () => {
  let sessionId = localStorage.getItem('evite_anonymous_session_id');
  if (!sessionId) {
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('evite_anonymous_session_id', sessionId);
  }
  return sessionId;
};

// Get device info for better tracking
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let device = 'Desktop';
  if (/Android/i.test(ua)) device = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) device = 'iOS';
  else if (/Mobile/i.test(ua)) device = 'Mobile';
  
  return {
    device,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    language: navigator.language
  };
};

// Get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || localStorage.getItem('utm_source'),
    utm_medium: params.get('utm_medium') || localStorage.getItem('utm_medium'),
    utm_campaign: params.get('utm_campaign') || localStorage.getItem('utm_campaign'),
    referrer: document.referrer || null
  };
};

// Store UTM params in localStorage for the session
const storeUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('utm_source')) {
    localStorage.setItem('utm_source', params.get('utm_source'));
  }
  if (params.get('utm_medium')) {
    localStorage.setItem('utm_medium', params.get('utm_medium'));
  }
  if (params.get('utm_campaign')) {
    localStorage.setItem('utm_campaign', params.get('utm_campaign'));
  }
};

// Track a page view with UTM data and anonymous session
const trackPageView = async (path) => {
  try {
    // Store UTM params on first visit
    storeUtmParams();
    
    const sessionToken = localStorage.getItem('session_token');
    const utmParams = getUtmParams();
    const anonymousSessionId = getAnonymousSessionId();
    const deviceInfo = getDeviceInfo();
    
    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
      },
      body: JSON.stringify({
        event_type: 'page_view',
        path: path,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        referrer: utmParams.referrer,
        anonymous_session_id: sessionToken ? null : anonymousSessionId,
        device_info: deviceInfo
      })
    });
  } catch (error) {
    // Silent fail - analytics is non-critical
    console.debug('Analytics tracking failed:', error);
  }
};

// Track a search query
export const trackSearch = async (query, path) => {
  if (!query || !query.trim()) return;
  
  try {
    const sessionToken = localStorage.getItem('session_token');
    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
      },
      body: JSON.stringify({
        event_type: 'search',
        search_query: query,
        path: path
      })
    });
  } catch (error) {
    // Silent fail
    console.debug('Search tracking failed:', error);
  }
};

// Hook to automatically track page views
export const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location.pathname]);
};

// Hook to get trackSearch function with current path
export const useSearchTracking = () => {
  const location = useLocation();
  
  const trackSearchQuery = useCallback((query) => {
    trackSearch(query, location.pathname);
  }, [location.pathname]);
  
  return trackSearchQuery;
};

export default usePageTracking;
